import React, { useEffect } from 'react';

import 'react-big-calendar/lib/sass/styles.scss';
import { SendBirdProvider } from '@sendbird/uikit-react';
import { Settings } from 'luxon';

import {
  Language,
  getLanguagesInfo,
  getPaymentLoadStatus,
  language,
  preLoginLang,
  setPreLoginLangWeb,
  userIdSelector,
} from '@pharmaplan/common';
import actionTracker from '@pharmaplan/common/actionTracker';

import { Toaster } from 'react-hot-toast';
import LogoutHandler from '@pharmaplan/common/helpers/LogoutHandler';
import { useLocation } from 'react-router-dom';
import StripeWrapper from './Stripe/StripeWrapper';
import AppRouter from './AppRouter/AppRouter';
import Loader from './components/Loader/Loader';
import { useAppSelector } from './hooks/useAppSelector';
import { useAppDispatch } from './hooks/useAppDispatch';
import useLanguage from './hooks/useLanguge';
import useErrorHandler from './hooks/useErrorHandler';
import { isLoadingSelector } from './selectors/statusSelector';
import strings from './localization';

import { Constants, myColorSet, myStringSet } from './helpers/Constants';
import { getBrowserLang } from './helpers/Functions';
import useLogoutCheck from './hooks/useLogoutCheck';
import useAdmin from './hooks/useAdmin';
import './App.css';
import './Firebase';
import AnalyticsProvider from './analytics/AnalyticsProvider';
import useLoggedOutUrls from './hooks/useLoggedOutUrls';

const { en } = Language;
const { REACT_APP_SENDBIRD_API_KEY } = process.env ?? {};

const App = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const userId = useAppSelector(userIdSelector);
  const { isAdmin } = useAdmin();

  const browserLang = getBrowserLang();
  const { cookies } = useLanguage();
  useErrorHandler();

  const lang = useAppSelector(language);
  const isUserLoggedIn = LogoutHandler.isUserLoggedIn();
  const preLogin = useAppSelector(preLoginLang);
  const load = useAppSelector((state) =>
    isLoadingSelector(actionTracker, state));
  const paymentLoad = useAppSelector(getPaymentLoadStatus);

  const notPublicShifts = pathname !== Constants.paths.availableShifts
    && pathname !== Constants.paths.availableShiftsFrench;

  const { language: cookieLanguage } = cookies ?? {};
  const userLang = isUserLoggedIn && notPublicShifts ? lang : preLogin;
  Settings.defaultLocale = userLang;

  useEffect(() => {
    if (notPublicShifts) {
      dispatch(setPreLoginLangWeb(cookieLanguage || browserLang));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookieLanguage]);

  useEffect(() => {
    dispatch(getLanguagesInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLoggedOutUrls();
  useLogoutCheck();

  if (!isAdmin) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    delete myStringSet.MESSAGE_INPUT__PLACE_HOLDER__DISABLED;
  }

  return (
    <StripeWrapper>
      <SendBirdProvider
        appId={REACT_APP_SENDBIRD_API_KEY ?? ''}
        userId={userId}
        stringSet={myStringSet}
        uikitOptions={{
          groupChannelList: {
            enableMessageReceiptStatus: true,
          },
        }}
        colorSet={myColorSet}
      >
        <>
          <Loader show={paymentLoad || load} />
          {strings.setLanguage(userLang || en)}
          <Toaster />
          <div className="App">
            <AnalyticsProvider>
              <AppRouter />
            </AnalyticsProvider>
          </div>
        </>
      </SendBirdProvider>
    </StripeWrapper>
  );
};

export default App;
